/**
 * Contract Bridge — server-authoritative rules engine (four players, Chicago scoring over a fixed number of deals).
 *
 * Rules implemented (toggles live in {@link BridgeConfig}):
 *  - Seats 0 & 2 (North–South, team 0) against seats 1 & 3 (East–West, team 1). One 52-card deck, 13 cards each.
 *    The dealer rotates every deal.
 *  - Auction: starting with the dealer, each player passes, bids a level (1–7) and strain (♣ ♦ ♥ ♠ NT) higher than
 *    the last bid, doubles an opponent's bid or redoubles a double on their own side's bid. It ends after three
 *    passes in a row following a call (or four passes with no bid, which scores nothing). The declarer is the first
 *    player of the winning side to have named the contract's strain.
 *  - Play: the player left of the declarer makes the opening lead, then the declarer's partner (the dummy) lays their
 *    hand face up and the declarer plays both hands. Follow suit when able; the highest trump, or failing that the
 *    highest card of the led suit, wins the trick.
 *  - Scoring per deal: see score.ts. Vulnerability follows the Chicago schedule (deal 1 none, deals 2 and 3 the dealer's
 *    side, deal 4 both, repeating). Highest total after the last deal wins; partners share a place.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId, type Suit } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';
import { BRIDGE_STRAINS, bridgeScoreDeal, type BridgeContract, type BridgeDealScore, type BridgeStrain } from './score.js';

export interface BridgeConfig {
  /** Deals in a match. */
  deals: number;
  trickEndDelayMs: number;
  dealEndDelayMs: number;
}

export type BridgePhase = 'BIDDING' | 'PLAYING' | 'TRICK_END' | 'DEAL_END' | 'MATCH_END';

export type BridgeCall = { kind: 'pass' } | { kind: 'double' } | { kind: 'redouble' } | { kind: 'bid'; level: number; strain: BridgeStrain };

export interface BridgeCallRecord {
  seat: number;
  call: BridgeCall;
}

export interface BridgeFinalContract extends BridgeContract {
  declarer: number;
}

export interface BridgeTrickPlay {
  seat: number;
  card: CardId;
}

export interface BridgeCompletedTrick {
  leader: number;
  plays: BridgeTrickPlay[];
  winner: number;
}

export interface BridgeDealResult {
  dealNo: number;
  dealer: number;
  /** Vulnerability of [team 0, team 1] in this deal. */
  vulnerable: [boolean, boolean];
  contract: BridgeFinalContract | null;
  /** Tricks taken by the declaring side. */
  tricks: number;
  score: BridgeDealScore | null;
  /** Points each team gained [team 0, team 1]. */
  delta: [number, number];
}

export interface BridgeState {
  config: BridgeConfig;
  players: string[];
  dealNo: number;
  dealer: number;
  phase: BridgePhase;
  hands: CardId[][];
  calls: BridgeCallRecord[];
  /** Seat to call. */
  turn: number;
  contract: BridgeFinalContract | null;
  /** Seat whose card is next (differs from the actor when the declarer plays the dummy). */
  toPlay: number;
  trick: BridgeTrickPlay[];
  tricks: BridgeCompletedTrick[];
  tricksWon: [number, number];
  dummyRevealed: boolean;
  teamScores: [number, number];
  dealResults: BridgeDealResult[];
}

export type BridgeAction = { type: 'call'; call: BridgeCall } | { type: 'play'; card: CardId };

export type BridgeLegal =
  | { kind: 'bid'; lastBid: { level: number; strain: BridgeStrain; seat: number } | null; canDouble: boolean; canRedouble: boolean }
  | { kind: 'play'; from: number; cards: CardId[] }
  | { kind: 'none' };

export interface BridgeView {
  gameType: 'BRIDGE';
  config: BridgeConfig;
  seat: number | null;
  phase: BridgePhase;
  dealNo: number;
  dealer: number;
  vulnerable: [boolean, boolean];
  calls: BridgeCallRecord[];
  /** Seat whose call it is (bidding) or whose card is next (play). */
  turn: number;
  /** Seat whose hand the next card comes from. */
  toPlay: number;
  contract: BridgeFinalContract | null;
  dummy: number | null;
  /** The dummy's cards, face up once the opening lead is made. */
  dummyHand: CardId[] | null;
  hand: CardId[];
  handCounts: number[];
  trick: BridgeTrickPlay[];
  lastTrick: BridgeCompletedTrick | null;
  tricks: BridgeCompletedTrick[];
  tricksWon: [number, number];
  teamScores: [number, number];
  dealResults: BridgeDealResult[];
  totals: number[];
}

const DEFAULTS: BridgeConfig = {
  deals: 4,
  trickEndDelayMs: 900,
  dealEndDelayMs: 7000,
};

const SEATS = 4;

export const bridgeTeamOf = (seat: number): number => seat % 2;
export const bridgePartnerOf = (seat: number): number => (seat + 2) % SEATS;

/** Chicago vulnerability for a deal: none, dealer's side, dealer's side, both (repeating). */
export function bridgeVulnerability(dealNo: number, dealer: number): [boolean, boolean] {
  const step = (dealNo - 1) % 4;
  if (step === 0) return [false, false];
  if (step === 3) return [true, true];
  const t = bridgeTeamOf(dealer);
  return t === 0 ? [true, false] : [false, true];
}

export const bridgeTrump = (strain: BridgeStrain): Suit | null => (strain === 'N' ? null : (strain as Suit));

/** High-card points: A 4, K 3, Q 2, J 1. */
export function bridgeHcp(hand: readonly CardId[]): number {
  return hand.reduce((n, c) => {
    const r = parseCard(c).rank;
    return n + (r === 14 ? 4 : r === 13 ? 3 : r === 12 ? 2 : r === 11 ? 1 : 0);
  }, 0);
}

export const bridgeCallRank = (level: number, strain: BridgeStrain): number => level * 5 + BRIDGE_STRAINS.indexOf(strain);

export function bridgeCallText(c: BridgeCall): string {
  return c.kind === 'pass' ? 'Pass' : c.kind === 'double' ? 'X' : c.kind === 'redouble' ? 'XX' : `${c.level}${c.strain === 'N' ? 'NT' : c.strain}`;
}

/** What is open to the next caller, given the calls so far. */
export function bridgeAuctionInfo(calls: readonly BridgeCallRecord[], seat: number): { lastBid: { level: number; strain: BridgeStrain; seat: number } | null; canDouble: boolean; canRedouble: boolean } {
  let lastBid: { level: number; strain: BridgeStrain; seat: number } | null = null;
  let doubled: 0 | 1 | 2 = 0;
  for (const { seat: s, call } of calls) {
    if (call.kind === 'bid') {
      lastBid = { level: call.level, strain: call.strain, seat: s };
      doubled = 0;
    } else if (call.kind === 'double') doubled = 1;
    else if (call.kind === 'redouble') doubled = 2;
  }
  const opp = lastBid !== null && bridgeTeamOf(lastBid.seat) !== bridgeTeamOf(seat);
  return { lastBid, canDouble: lastBid !== null && opp && doubled === 0, canRedouble: lastBid !== null && !opp && doubled === 1 };
}

/** Is a bid of `level` `strain` legal after `lastBid`? */
export const bridgeBidAllowed = (lastBid: { level: number; strain: BridgeStrain } | null, level: number, strain: BridgeStrain): boolean =>
  level >= 1 && level <= 7 && (lastBid === null || bridgeCallRank(level, strain) > bridgeCallRank(lastBid.level, lastBid.strain));

/** Has the auction finished? */
function auctionOver(calls: readonly BridgeCallRecord[]): boolean {
  if (calls.length < 4) return false;
  const lastThree = calls.slice(-3).every((c) => c.call.kind === 'pass');
  if (calls.length === 4 && calls.every((c) => c.call.kind === 'pass')) return true;
  return lastThree && calls.some((c) => c.call.kind !== 'pass');
}

function finalContract(calls: readonly BridgeCallRecord[]): BridgeFinalContract | null {
  let last: { level: number; strain: BridgeStrain; seat: number } | null = null;
  let doubled: 0 | 1 | 2 = 0;
  for (const { seat, call } of calls) {
    if (call.kind === 'bid') {
      last = { level: call.level, strain: call.strain, seat };
      doubled = 0;
    } else if (call.kind === 'double') doubled = 1;
    else if (call.kind === 'redouble') doubled = 2;
  }
  if (!last) return null;
  const team = bridgeTeamOf(last.seat);
  const first = calls.find((c) => c.call.kind === 'bid' && c.call.strain === last!.strain && bridgeTeamOf(c.seat) === team)!;
  return { level: last.level, strain: last.strain, doubled, declarer: first.seat };
}

/** Cards that may be played from `hand` onto `trick`. */
export function bridgeLegalPlay(hand: readonly CardId[], trick: readonly BridgeTrickPlay[]): CardId[] {
  if (trick.length === 0) return [...hand];
  const lead = parseCard(trick[0]!.card).suit;
  const followers = hand.filter((c) => parseCard(c).suit === lead);
  return followers.length > 0 ? followers : [...hand];
}

export function bridgeTrickWinner(plays: readonly BridgeTrickPlay[], trump: Suit | null): number {
  const lead = parseCard(plays[0]!.card).suit;
  let best = plays[0]!;
  for (const p of plays.slice(1)) {
    const c = parseCard(p.card);
    const b = parseCard(best.card);
    const beats = (trump !== null && c.suit === trump && b.suit !== trump) || (c.suit === b.suit && c.rank > b.rank);
    if (beats && (c.suit === lead || c.suit === trump)) best = p;
  }
  return best.seat;
}

function deal(state: BridgeState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck(), rng);
  const hands: CardId[][] = [[], [], [], []];
  deck.forEach((c, i) => hands[i % SEATS]!.push(c));
  state.hands = hands.map((h) => sortHand(h));
  state.calls = [];
  state.contract = null;
  state.trick = [];
  state.tricks = [];
  state.tricksWon = [0, 0];
  state.dummyRevealed = false;
  state.phase = 'BIDDING';
  state.turn = state.dealer;
  state.toPlay = state.dealer;
  events.push({ type: 'dealt', dealNo: state.dealNo, dealer: state.dealer, vulnerable: bridgeVulnerability(state.dealNo, state.dealer) });
}

/** The seat whose input the engine is waiting for. */
function actorOf(state: BridgeState): number {
  if (state.phase === 'BIDDING') return state.turn;
  const c = state.contract;
  return c && state.toPlay === bridgePartnerOf(c.declarer) ? c.declarer : state.toPlay;
}

export const bridgeEngine: GameEngine<BridgeConfig, BridgeState, BridgeAction, BridgeView, BridgeLegal> = {
  gameType: 'BRIDGE',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('deals', c.deals, 1, 16);
    checkInt('trickEndDelayMs', c.trickEndDelayMs, 0, 10000);
    checkInt('dealEndDelayMs', c.dealEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Bridge needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: BridgeState = {
      config,
      players,
      dealNo: 1,
      dealer: rng.int(SEATS),
      phase: 'BIDDING',
      hands: [],
      calls: [],
      turn: 0,
      contract: null,
      toPlay: 0,
      trick: [],
      tricks: [],
      tricksWon: [0, 0],
      dummyRevealed: false,
      teamScores: [0, 0],
      dealResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'play' && typeof raw.card === 'string' && raw.card.length <= 4) return { type: 'play', card: raw.card };
    if (raw.type === 'call' && isPlainObject(raw.call)) {
      const c = raw.call;
      if (c.kind === 'pass' || c.kind === 'double' || c.kind === 'redouble') return { type: 'call', call: { kind: c.kind } };
      if (c.kind === 'bid' && typeof c.level === 'number' && Number.isInteger(c.level) && typeof c.strain === 'string' && (BRIDGE_STRAINS as readonly string[]).includes(c.strain)) {
        return { type: 'call', call: { kind: 'bid', level: c.level, strain: c.strain as BridgeStrain } };
      }
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Bridge action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END', 'GAME_OVER', 'Match is over');
    assertEngine((state.phase === 'BIDDING' || state.phase === 'PLAYING') && seat === actorOf(state), 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];

    if (action.type === 'call') {
      assertEngine(state.phase === 'BIDDING', 'INVALID_PHASE', 'The auction is over');
      const info = bridgeAuctionInfo(state.calls, seat);
      const call = action.call;
      if (call.kind === 'bid') assertEngine(bridgeBidAllowed(info.lastBid, call.level, call.strain), 'ILLEGAL_MOVE', 'That bid is not high enough');
      if (call.kind === 'double') assertEngine(info.canDouble, 'ILLEGAL_MOVE', 'You cannot double now');
      if (call.kind === 'redouble') assertEngine(info.canRedouble, 'ILLEGAL_MOVE', 'You cannot redouble now');
      state.calls.push({ seat, call });
      events.push({ type: 'call', seat, call });
      if (auctionOver(state.calls)) {
        const contract = finalContract(state.calls);
        if (!contract) {
          // Passed out: nothing is scored.
          endDeal(state, events);
          return events;
        }
        state.contract = contract;
        state.phase = 'PLAYING';
        state.toPlay = (contract.declarer + 1) % SEATS;
        events.push({ type: 'auctionOver', contract });
      } else {
        state.turn = (seat + 1) % SEATS;
      }
      return events;
    }

    assertEngine(state.phase === 'PLAYING', 'INVALID_PHASE', 'Not in play phase');
    const from = state.toPlay;
    const hand = state.hands[from]!;
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in that hand');
    assertEngine(bridgeLegalPlay(hand, state.trick).includes(action.card), 'ILLEGAL_MOVE', 'You must follow suit');
    removeCards(hand, [action.card]);
    state.trick.push({ seat: from, card: action.card });
    events.push({ type: 'cardPlayed', seat: from, card: action.card, by: seat });
    if (!state.dummyRevealed) {
      state.dummyRevealed = true;
      events.push({ type: 'dummy', seat: bridgePartnerOf(state.contract!.declarer) });
    }
    if (state.trick.length < SEATS) {
      state.toPlay = (from + 1) % SEATS;
      return events;
    }
    const winner = bridgeTrickWinner(state.trick, bridgeTrump(state.contract!.strain));
    state.tricksWon[bridgeTeamOf(winner)]!++;
    state.tricks.push({ leader: state.trick[0]!.seat, plays: state.trick, winner });
    events.push({ type: 'trickWon', seat: winner, trickNo: state.tricks.length });
    state.phase = 'TRICK_END';
    state.toPlay = winner;
    return events;
  },

  getActors(state) {
    return state.phase === 'BIDDING' || state.phase === 'PLAYING' ? [actorOf(state)] : [];
  },

  getLegalActions(state, seat) {
    if (seat !== actorOf(state)) return { kind: 'none' };
    if (state.phase === 'BIDDING') return { kind: 'bid', ...bridgeAuctionInfo(state.calls, seat) };
    if (state.phase === 'PLAYING') return { kind: 'play', from: state.toPlay, cards: bridgeLegalPlay(state.hands[state.toPlay]!, state.trick) };
    return { kind: 'none' };
  },

  getTimeoutAction(state) {
    if (state.phase === 'BIDDING') return { type: 'call', call: { kind: 'pass' } };
    const legal = bridgeLegalPlay(state.hands[state.toPlay]!, state.trick);
    const lowest = [...legal].sort((a, b) => parseCard(a).rank - parseCard(b).rank)[0]!;
    return { type: 'play', card: lowest };
  },

  getView(state, seat) {
    const dummy = state.contract ? bridgePartnerOf(state.contract.declarer) : null;
    const playing = state.phase === 'PLAYING' || state.phase === 'TRICK_END';
    return {
      gameType: 'BRIDGE',
      config: state.config,
      seat,
      phase: state.phase,
      dealNo: state.dealNo,
      dealer: state.dealer,
      vulnerable: bridgeVulnerability(state.dealNo, state.dealer),
      calls: state.calls.map((c) => ({ seat: c.seat, call: { ...c.call } })),
      turn: state.phase === 'BIDDING' || state.phase === 'PLAYING' ? actorOf(state) : state.toPlay,
      toPlay: state.toPlay,
      contract: state.contract ? { ...state.contract } : null,
      dummy,
      dummyHand: playing && state.dummyRevealed && dummy !== null ? [...state.hands[dummy]!] : null,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      trick: state.phase === 'TRICK_END' ? [...(state.tricks.at(-1)?.plays ?? [])] : [...state.trick],
      lastTrick: state.tricks.at(-1) ?? null,
      tricks: state.tricks.map((t) => ({ ...t, plays: [...t.plays] })),
      tricksWon: [...state.tricksWon],
      teamScores: [...state.teamScores],
      dealResults: state.dealResults.map((r) => ({ ...r, vulnerable: [...r.vulnerable] as [boolean, boolean], delta: [...r.delta] as [number, number] })),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[bridgeTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'TRICK_END') return { kind: 'collectTrick', delayMs: state.config.trickEndDelayMs };
    if (state.phase === 'DEAL_END') return { kind: 'nextRound', delayMs: state.config.dealEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'TRICK_END') {
      state.trick = [];
      if (state.tricks.length < 13) {
        state.phase = 'PLAYING';
        events.push({ type: 'trickCollected', leader: state.toPlay });
        return events;
      }
      endDeal(state, events);
      return events;
    }
    if (state.phase === 'DEAL_END') {
      state.dealNo++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.dealResults.length,
};

function endDeal(state: BridgeState, events: EngineEvent[]): void {
  const vulnerable = bridgeVulnerability(state.dealNo, state.dealer);
  const c = state.contract;
  let result: BridgeDealResult;
  if (!c) {
    result = { dealNo: state.dealNo, dealer: state.dealer, vulnerable, contract: null, tricks: 0, score: null, delta: [0, 0] };
  } else {
    const team = bridgeTeamOf(c.declarer);
    const tricks = state.tricksWon[team]!;
    const score = bridgeScoreDeal(c, vulnerable[team]!, tricks);
    const delta: [number, number] = [0, 0];
    delta[team] = score.declarer;
    delta[1 - team] = score.defenders;
    result = { dealNo: state.dealNo, dealer: state.dealer, vulnerable, contract: { ...c }, tricks, score, delta };
  }
  state.teamScores[0] += result.delta[0];
  state.teamScores[1] += result.delta[1];
  state.dealResults.push(result);
  events.push({ type: 'dealEnd', result, teamScores: [...state.teamScores] });
  if (state.dealNo >= state.config.deals) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: standings(state) });
  } else {
    state.phase = 'DEAL_END';
  }
}

/** Partners share a place: the higher total is 1st, the other 2nd (both 1st on a tie). */
function standings(state: BridgeState): Standing[] {
  const [a, b] = state.teamScores;
  return [0, 1, 2, 3].map((seat) => {
    const team = bridgeTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}
